import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { toast } from "../../../components/AppToast";
import { createCustomerGroupAPI, updateCustomerGroupAPI } from "../../../services/customer-group/customerGroup";
import "./customerGroup.css";

const PLACEHOLDER_SELECT = "Select…";

const statusFromApi = (s) => {
  const l = String(s ?? "Active").trim().toLowerCase();
  return l === "inactive" ? "Inactive" : "Active";
};

export default function CreateCustomerGroup({ onClose, onSuccess, editData, readOnly = false }) {
  const { userDetail } = useSelector((state) => state?.login || {});
  const recordId = editData?.id ?? editData?._id;
  const isEdit = !!recordId && !readOnly;

  const [formData, setFormData] = useState({
    customerGroupcode: "",
    customerGroupName: "",
    customerGroupDescription: "",
    status: "Active",
    createDate: new Date().toISOString().split("T")[0],
    createdBy: userDetail?.userName || userDetail?.email || userDetail?.name || userDetail?.userId || "Unknown User",
  });

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const ed = editData;
    const editMode = !!(ed && (ed.id ?? ed._id));
    const createdByVal = editMode
      ? ed.createdBy || ed.created_by || "—"
      : userDetail?.userName || userDetail?.email || userDetail?.name || userDetail?.userId || "Unknown User";
    const createDateVal = editMode
      ? ed.createDate || ed.createdAt?.split?.("T")?.[0] || ed.created_at?.split?.("T")?.[0] || "—"
      : new Date().toISOString().split("T")[0];

    setFormData({
      customerGroupcode: ed?.customerGroupcode || ed?.code || ed?.customerGroupCode || "",
      customerGroupName: ed?.customerGroupName || ed?.name || "",
      customerGroupDescription: ed?.customerGroupDescription || ed?.description || "",
      status: statusFromApi(ed?.status),
      createDate: createDateVal,
      createdBy: createdByVal,
    });
    setErrors({});
  }, [editData, userDetail]);

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!isEdit && !formData.customerGroupcode.trim()) {
      newErrors.customerGroupcode = "Supplier Group Code is required";
    }
    if (!formData.customerGroupName.trim()) {
      newErrors.customerGroupName = "Supplier Group Name is required";
    }
    if (!formData.customerGroupDescription.trim()) {
      newErrors.customerGroupDescription = "Supplier Group Description is required";
    }
    if (!formData.status) {
      newErrors.status = "Status is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCustomerGroupAPI(formData, recordId);
        toast.success("Supplier Group updated successfully");
      } else {
        await createCustomerGroupAPI(formData);
        toast.success("Supplier Group created successfully");
      }
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      console.error("Error saving customer group:", err);
      toast.error(typeof err === "string" ? err : "Failed to save customer group");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="create-customer-group-card border-0">
      <Card.Header className="bg-white border-bottom">
        <h5 className="mb-0">
          {readOnly ? "View Supplier Group" : isEdit ? "Edit Supplier Group" : "Create Supplier Group"}
        </h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className={!isEdit ? "required" : undefined}>Supplier Group Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter supplier group code"
                  value={formData.customerGroupcode}
                  onChange={(e) => handleInputChange("customerGroupcode", e.target.value)}
                  isInvalid={!!errors.customerGroupcode}
                  disabled={isEdit || readOnly}
                  readOnly={readOnly}
                  required={!isEdit}
                />
                {errors.customerGroupcode && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.customerGroupcode}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Supplier Group Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter supplier group name"
                  value={formData.customerGroupName}
                  onChange={(e) => handleInputChange("customerGroupName", e.target.value)}
                  isInvalid={!!errors.customerGroupName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  required
                />
                {errors.customerGroupName && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.customerGroupName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Supplier Group Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter supplier group description"
                  value={formData.customerGroupDescription}
                  onChange={(e) => handleInputChange("customerGroupDescription", e.target.value)}
                  isInvalid={!!errors.customerGroupDescription}
                  readOnly={readOnly}
                  disabled={readOnly}
                  required
                />
                {errors.customerGroupDescription && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.customerGroupDescription}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  isInvalid={!!errors.status}
                  disabled={readOnly}
                  required
                >
                  <option value="">{PLACEHOLDER_SELECT}</option>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {errors.status && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.status}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Create Date</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Auto-generated"
                  value={formData.createDate}
                  disabled
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Current user"
                  value={formData.createdBy}
                  disabled
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting
                  ? isEdit
                    ? "Updating..."
                    : "Creating..."
                  : isEdit
                    ? "Update Supplier Group"
                    : "Create Supplier Group"}
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
